import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, ArrowRight } from "lucide-react";
import FloatingShapes from "@/components/FloatingShapes";
import Footer from "@/components/Footer";
import AdsterraBanner from "@/components/AdsterraBanner";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";

// Adsterra ad unit HTML — injected only when show_ads is true (free owner's deck)
const AD_NATIVE = `<script async="async" data-cfasync="false" src="https://bauval.org/21/00c8a7d4609b2d904bade74547ab57e6"></script><div id="container-00c8a7d4609b2d904bade74547ab57e6"></div>`;
const AD_728x90 = `<script>atOptions = {'key':'9cf67bec3b36ad7fc9e6db318733cf1c','format':'iframe','height':90,'width':728,'params':{}}</script><script src="https://bauval.org/22/9cf67bec3b36ad7fc9e6db318733cf1c"></script>`;
const AD_300x250 = `<script>atOptions = {'key':'c78ee766345813ed7d53839c360c3ddd','format':'iframe','height':250,'width':300,'params':{}}</script><script src="https://bauval.org/22/c78ee766345813ed7d53839c360c3ddd"></script>`;

// Card dimensions: 480px wide × 600px tall on desktop; 90vw wide on mobile
const CARD_WIDTH_DESKTOP = 480;
const CARD_HEIGHT = 600;

// Public, unauthenticated read-only deck view reached via a share link.
export default function SharedDeck() {
  const { shareId } = useParams();
  const [deck, setDeck] = useState(null);
  const [err, setErr] = useState("");
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    (async () => {
      try {
        const r = await api.get(`/public/deck/${shareId}`);
        setDeck(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "This shared deck isn't available.");
      }
    })();
  }, [shareId]);

  const Cta = ({ testid }) => (
    <Link to="/" className="btn-primary inline-flex items-center gap-2" data-testid={testid}>
      <Sparkles className="w-4 h-4" /> Create Your Own Free Study Deck
    </Link>
  );

  const TopBar = () => (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-stone-100">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        <Link to="/" className="flex items-center gap-2" data-testid="shared-home-link">
          <img src="/logo.jpg" alt="LoomPDF logo" className="w-7 h-7 rounded-lg object-cover" />
          <div className="font-display text-xl">LoomPDF</div>
        </Link>
        <Cta testid="shared-topbar-cta" />
      </div>
    </div>
  );

  if (err) {
    return (
      <div className="relative min-h-screen grain" data-testid="shared-deck-error">
        <FloatingShapes /><TopBar />
        <main className="relative z-10 max-w-xl mx-auto px-6 pt-40 text-center">
          <h1 className="font-display text-4xl">Deck unavailable</h1>
          <p className="text-stone-600 mt-4">{err}</p>
          <div className="mt-8"><Cta testid="shared-error-cta" /></div>
        </main>
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="min-h-screen flex items-center justify-center font-hand text-3xl text-stone-500" data-testid="shared-deck-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-3" /> unfurling…
      </div>
    );
  }

  // Prefer AI-generated title; fall back to stored filename
  const displayTitle = (deck.doc_title && deck.doc_title.trim()) ? deck.doc_title : deck.name;
  const sections = deck.sections || [];

  // Card style: 480px × 600px desktop, 90vw × 600px mobile
  // Width handled via Tailwind responsive classes; height via inline style
  const cardStyle = {
    height: `${CARD_HEIGHT}px`,
    flexShrink: 0,
  };

  return (
    <div className="relative min-h-screen grain overflow-x-hidden" data-testid="shared-deck-page">
      <Helmet>
        <title>{displayTitle ? `${displayTitle} — LoomPDF` : "A LoomPDF study deck"}</title>
        <meta name="description" content="Study this horizontal deck free on LoomPDF, then turn your own PDFs into study decks and quizzes." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <FloatingShapes /><TopBar />

      <main className="relative z-10 pt-20 pb-16 max-w-6xl mx-auto px-6">
        {/* ── Page header: max-height 80px, title 1.2rem ── */}
        <div
          className="mt-4 overflow-hidden"
          style={{ maxHeight: "80px" }}
          data-testid="shared-deck-header"
        >
          <div className="flex items-center gap-3">
            <span className="tag-pill" data-testid="shared-badge">
              <Sparkles className="w-3 h-3 inline mr-1" /> shared deck
            </span>
            <span className="font-hand text-stone-400 text-sm">
              {deck.total_sections} rooms · scroll →
            </span>
          </div>
          <h1
            className="font-display leading-snug mt-1 truncate"
            style={{ fontSize: "1.2rem" }}
            data-testid="shared-deck-title"
          >
            {displayTitle}
          </h1>
        </div>

        {deck.locked && (
          <div className="sticky-card p-4 mt-4 border-l-4 border-amber-400" data-testid="shared-view-limit">
            <p className="text-stone-700 text-sm">
              You've used your {deck.free_views} free views. Here's a preview —
              create your own free deck to keep studying.
            </p>
          </div>
        )}

        {/* ── Horizontal card scroll ── */}
        <div className="h-scroll mt-4" style={{ paddingBottom: "1.5rem" }} data-testid="shared-scroll">
          {sections.map((s, i) => (
            <div
              key={s.id || i}
              data-scroll-card
              className="snap-card w-[90vw] lg:w-[480px]"
              style={cardStyle}
              data-testid={`shared-section-${i}`}
            >
              <motion.article
                initial={{ opacity: 0, y: 24, rotate: i % 2 ? 1 : -1 }}
                animate={{ opacity: 1, y: 0, rotate: i % 2 ? 0.5 : -0.5 }}
                transition={{ delay: i * 0.05, duration: 0.6 }}
                className="sticky-card relative overflow-y-auto overflow-x-hidden p-6"
                style={{
                  height: "100%",
                  background: `linear-gradient(180deg, ${(s.color || "#FEF08A")}55 0%, #ffffff 60%)`,
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="tag-pill">{String(i + 1).padStart(2, "0")} / {String(deck.total_sections).padStart(2, "0")}</span>
                  {s.page_range && <span className="font-hand text-stone-500 text-sm">p. {s.page_range}</span>}
                </div>
                <h2 className="font-display text-2xl mt-4 leading-tight">{s.title}</h2>
                {s.subtitle && <div className="font-hand text-stone-600 text-lg mt-1">{s.subtitle}</div>}
                <p className="text-stone-700 mt-4 leading-relaxed text-sm">{s.summary}</p>
                {s.key_points?.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {s.key_points.map((k, j) => (
                      <li key={j} className="flex gap-2 text-stone-800 text-sm">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                        <span><span className="marker">{k}</span></span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.details && (
                  <details className="mt-4 group">
                    <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] text-indigo-600">Deep recall ↓</summary>
                    <p className="text-stone-700 leading-relaxed mt-2 text-xs">{s.details}</p>
                  </details>
                )}
              </motion.article>
            </div>
          ))}

          {/* CTA card — same exact dimensions */}
          <div
            className="snap-card w-[90vw] lg:w-[480px] sticky-card flex flex-col items-center justify-center text-center p-8"
            data-scroll-card
            style={{ ...cardStyle, background: "linear-gradient(180deg,#C7D2FE55 0%,#fff 60%)" }}
            data-testid="shared-cta-card"
          >
            <Sparkles className="w-8 h-8 text-indigo-500" />
            <h2 className="font-display text-3xl mt-4">Make your own deck</h2>
            <p className="font-hand text-stone-500 text-xl mt-2">free · takes a minute</p>
            <div className="mt-6"><Cta testid="shared-card-cta" /></div>
          </div>
        </div>

        {/* ── Ads: scaled to 25% of original size (75% reduction), below scroll ── */}
        {deck.show_ads && (
          <div
            className="flex flex-col items-center gap-1"
            style={{ marginTop: "12px" }}
            data-testid="ad-container"
          >
            <div style={{ zoom: 0.25 }} data-testid="ad-native-banner">
              <AdsterraBanner html={AD_NATIVE} />
            </div>
            <div style={{ zoom: 0.25 }} data-testid="ad-banner-728">
              <AdsterraBanner html={AD_728x90} />
            </div>
            <div style={{ zoom: 0.25 }} data-testid="ad-banner-300">
              <AdsterraBanner html={AD_300x250} />
            </div>
          </div>
        )}

        <div className="mt-10 text-center">
          <Cta testid="shared-bottom-cta" />
          <p className="mt-4">
            <Link to="/pricing" className="text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid="shared-pricing-link">
              See plans <ArrowRight className="w-4 h-4" />
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
